/**
 * AI 결과 파싱 (AI_PROTOCOL §7·§13). 원문은 호출한 쪽이 항상 보존한다.
 * - 표식(BEGIN/END) 사이 JSON, 없으면 첫 '{'부터 마지막 '}'까지
 * - 코드펜스, 둥근 따옴표, 끝 쉼표 같은 흔한 형식 오류는 의미를 바꾸지 않는 범위에서 고친다
 * - 필드 형식이 조금 달라도(문자열 ↔ 배열 등) 최대한 받아들이고, 받을 수 없는 부분은 경고로 남긴다
 */
import { BEGIN, END } from './prompts';
import { isProfileField, type ProfileField } from '../workflow/profileMerge';
import { approachIdOf, evidenceKindOf, type ApproachId, type EvidenceKind } from '../workflow/approaches';

export type ParseOk<T> = { ok: true; value: T; warnings: string[] };
export type ParseFail = { ok: false; errors: string[] };

export function extractJson(raw: string): { text: string } | { error: string } {
  let t = raw.replace(/\r/g, '');
  const b = t.indexOf(BEGIN);
  const e = t.lastIndexOf(END);
  if (b >= 0 && e > b) t = t.slice(b + BEGIN.length, e);
  t = t.replace(/```(?:json)?/gi, '');
  const first = t.indexOf('{');
  const last = t.lastIndexOf('}');
  if (first < 0 || last <= first) return { error: 'JSON 객체를 찾을 수 없습니다' };
  return { text: t.slice(first, last + 1) };
}

const dropTrailingCommas = (t: string) => t.replace(/^\s+/, '').replace(/,\s*([}\]])/g, '$1');

/** 의미를 바꾸지 않는 형식 수선 (구조 자리에 쓰인 둥근 따옴표까지 바꾸는 마지막 단계) */
export function repairJsonText(t: string): string {
  return dropTrailingCommas(t).replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
}

/**
 * 응답이 중간에 끊긴 JSON 살리기: 마지막으로 완성된 항목까지만 남기고 열린 괄호를 닫는다.
 * 끊긴 문장 조각은 버린다 (내용을 지어내지 않음).
 */
export function closeTruncatedJson(text: string): string | null {
  const start = text.indexOf('{');
  if (start < 0) return null;
  const t = text.slice(start);
  const stack: string[] = [];
  let inStr = false;
  let esc = false;
  let lastSafe: { at: number; stack: string[] } | null = null;
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!;
    if (inStr) {
      if (esc) esc = false;
      else if (c === '\\') esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === '{' || c === '[') stack.push(c === '{' ? '}' : ']');
    else if (c === '}' || c === ']') {
      stack.pop();
      if (!stack.length) return t.slice(0, i + 1);
      lastSafe = { at: i + 1, stack: [...stack] };
    } else if (c === ',') lastSafe = { at: i, stack: [...stack] };
  }
  if (!lastSafe) return null;
  return dropTrailingCommas(t.slice(0, lastSafe.at).replace(/,\s*$/, '') + [...lastSafe.stack].reverse().join(''));
}

export function parseJsonLoose(
  raw: string,
): { ok: true; value: unknown; truncated?: boolean } | { ok: false; error: string } {
  const x0 = extractJson(raw);
  // 닫는 괄호가 하나도 없을 만큼 일찍 끊긴 응답도 아래 살리기로 넘긴다
  const x = 'error' in x0 && raw.includes('{') ? { text: '' } : x0;
  if ('error' in x) return { ok: false, error: x.error };
  // 순한 수선부터: 문장 속 “따옴표”는 가능하면 그대로 둔다
  for (const candidate of [x.text, dropTrailingCommas(x.text), repairJsonText(x.text)]) {
    try {
      return { ok: true, value: JSON.parse(candidate) };
    } catch {
      /* 다음 후보 */
    }
  }
  // 끊긴 응답: 끝 표식이 없거나 괄호가 덜 닫힘 → 완성된 부분만
  const tail = raw
    .replace(/\r/g, '')
    .replace(/```(?:json)?/gi, '')
    .split(END)[0]!;
  const fromBegin = tail.includes(BEGIN) ? tail.slice(tail.indexOf(BEGIN) + BEGIN.length) : tail;
  const closed = closeTruncatedJson(repairJsonText(fromBegin));
  if (closed) {
    try {
      return { ok: true, value: JSON.parse(closed), truncated: true };
    } catch {
      /* 아래 오류 */
    }
  }
  try {
    JSON.parse(repairJsonText(x.text));
  } catch (err) {
    return { ok: false, error: `JSON 형식 오류: ${(err as Error).message.slice(0, 120)}` };
  }
  return { ok: false, error: 'JSON 형식 오류' };
}

// ---------------- 느슨한 형 변환 ----------------

export const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : typeof v === 'number' ? String(v) : undefined;
export const strArr = (v: unknown): string[] => {
  if (Array.isArray(v))
    return v
      .map((x) =>
        typeof x === 'string'
          ? x.trim()
          : typeof x === 'object' && x && 'text' in x
            ? String((x as { text: unknown }).text)
            : '',
      )
      .filter(Boolean);
  if (typeof v === 'string' && v.trim())
    return v
      .split(/\n+/)
      .map((x) => x.replace(/^\s*[-•·*]\s*/, '').trim())
      .filter(Boolean);
  return [];
};
/** 사례개념화 칸 이름 (addedItems에서 받는 것) */
const FORMULATION_FIELD_KEYS = new Set([
  'currentProblems',
  'precipitatingFactors',
  'predisposingFactors',
  'perpetuatingFactors',
  'protectiveFactors',
  'coreEmotions',
  'coreCognitiveThemes',
  'beliefHypotheses',
  'behavioralPatterns',
  'relationalPatterns',
  'allianceFactors',
  'therapyInterferingPatterns',
]);

const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const objArr = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter((x) => x && typeof x === 'object').map((x) => x as Record<string, unknown>) : [];
const bool = (v: unknown) => v === true || v === 'true';

const RISK_STATUS = new Set(['not_assessed', 'denied', 'present', 'unclear']);
const GOAL_STATUS = new Set([
  'not_started',
  'exploring',
  'progressing',
  'maintaining',
  'achieved',
  'paused',
  'terminated',
]);
const DIAG_STATUS = new Set([
  'insufficient_data',
  'under_review',
  'possible',
  'professional_evaluation_recommended',
  'externally_confirmed',
]);

export type SessionAnalysisV1 = {
  clientCode?: string;
  sessionNumber?: number;
  sessionSummary?: string;
  keyEvents: string[];
  notableChanges: string[];
  clientReports: { text: string; segmentIds: string[] }[];
  counselorObservations: { text: string }[];
  hypotheses: { text: string; segmentIds: string[] }[];
  emotions: string[];
  cognitions: string[];
  behaviors: string[];
  relationshipPatterns: string[];
  interventionsUsed: { name: string; orientation?: string; clientResponse?: string; followUpNeeded: boolean }[];
  risk: Record<string, { status: string; evidence?: string; followUpRequired: boolean }>;
  protectiveFactors: string[];
  goalProgress: { goalId: string; proposedStatus: string; note?: string }[];
  formulation: {
    currentProblems: string[];
    added: string[];
    /** 칸별 새 항목 (신념 가설 밖의 칸: 촉발·취약·유지·보호요인, 핵심 정서 등) */
    addedItems: Record<string, string[]>;
    strengthened: string[];
    weakened: string[];
    rejected: string[];
    changeReason?: string;
  } | null;
  diagnosis: {
    symptomList: string[];
    considerations: {
      diagnosisName: string;
      status: string;
      criteriaToAssess: string[];
      differentialConsiderations: string[];
    }[];
  } | null;
  journalDraft: Record<string, string>;
  nextSessionBrief: {
    sessionObjectives: string[];
    assessmentQuestions: { question: string; purpose?: string }[];
    hypothesesToTest: string[];
    riskFollowUp: string[];
    unfinishedTopics: string[];
  };
  recommendedInterventions: {
    intervention: string;
    orientation?: string;
    rationale?: string;
    expectedGoal?: string;
    howTo?: string;
    caution?: string;
  }[];
  roleReviewProposals: { segmentId: string; proposedRole: 'counselor' | 'client'; reason?: string }[];
  /** A33: 치료 접근 근거 */
  approachEvidence: {
    approach: ApproachId;
    kind: EvidenceKind;
    quote: string;
    reason?: string;
    segmentIds: string[];
  }[];
  verbatimSuggestions: {
    startSegmentId: string;
    endSegmentId: string;
    reason?: string;
    supervisionQuestions: string[];
  }[];
  questionsForCounselorReview: string[];
  profileUpdates: { field: ProfileField; value: string; segmentIds: string[]; changed?: boolean }[];
  /** V2: 변화 지점 (축어록·대화록 구간의 근거) */
  changePoints: ChangePoint[];
  /** V2: 첫 번째 변화 지점 구간의 대화록 초안 (칸 키 → 내용) */
  dialogueDraft: Record<string, string>;
  /** V2: 같은 응답 안의 자기 점검 (이전 Claude 2차 검토) */
  selfReview: SecondReviewV1 | null;
  /** V2: 상담자가 원하는 방향에 맞춘 진행 계획 */
  directionPlan: {
    summary?: string;
    steps: { technique: string; theory?: string; method?: string; example?: string; caution?: string }[];
  } | null;
  /** 새 상담목표 제안 (목표가 없을 때 자동 추가) */
  goalSuggestions: { title: string; agreedWithClient: boolean; rationale?: string }[];
  /** 응답이 끊겨 일부만 받았을 때 빠진 항목 */
  missingSections: string[];
  truncated: boolean;
};

export type ChangePoint = {
  type: string;
  theory?: string;
  startSegmentId: string;
  endSegmentId: string;
  beforeSegmentIds: string[];
  afterSegmentIds: string[];
  whatChanged?: string;
  meaning?: string;
  supervisionQuestions: string[];
};

export const SESSION_SECTIONS = [
  'sessionSummary',
  'clientReports',
  'hypotheses',
  'interventionsUsed',
  'riskAssessmentProposal',
  'goalProgressProposals',
  'caseFormulationUpdate',
  'diagnosticConsiderationUpdate',
  'journalDraft',
  'nextSessionBrief',
  'recommendedInterventions',
  'changePoints',
  'dialogueDraft',
  'directionPlan',
  'selfReview',
  'clientProfileUpdates',
  'approachEvidence',
] as const;

const DIALOGUE_KEYS = ['preparation', 'observation', 'analysis', 'techniques', 'theology', 'plan'];
const REVIEW_KEYS = [
  'unsupportedInference',
  'alternativeHypotheses',
  'missingInformation',
  'contradictoryEvidence',
  'theoryBias',
  'interventionConcerns',
  'counselorProcessQuestions',
  'supervisionQuestions',
] as const;

const JOURNAL_KEYS = [
  'counselingReasonAndGoal',
  'assessment',
  'pastCounselingExperience',
  'importantIssue',
  'previousSessionIssueOrTask',
  'counselingContent',
  'nextSessionPlan',
  'remarks',
];

export function parseSessionAnalysis(raw: string): ParseOk<SessionAnalysisV1> | ParseFail {
  const j = parseJsonLoose(raw);
  if (!j.ok) return { ok: false, errors: [j.error] };
  const o = obj(j.value);
  const warnings: string[] = [];
  if (o.formatVersion !== 'SESSION_ANALYSIS_V1' && o.formatVersion !== 'SESSION_ANALYSIS_V2') {
    if (o.formatVersion === undefined) warnings.push('formatVersion이 없습니다 (SESSION_ANALYSIS_V2로 간주)');
    else return { ok: false, errors: [`다른 형식입니다: ${String(o.formatVersion)}`] };
  }
  if (j.truncated) warnings.push('응답이 중간에 끊겨 받은 부분까지만 읽었습니다');

  const risk: SessionAnalysisV1['risk'] = {};
  for (const [k, v] of Object.entries(obj(o.riskAssessmentProposal))) {
    const r = obj(v);
    const status = str(r.status) ?? '';
    if (!RISK_STATUS.has(status)) {
      if (status) warnings.push(`위험 항목 ${k}: 알 수 없는 상태 "${status}" 무시`);
      continue;
    }
    risk[k] = {
      status,
      ...(str(r.evidence) ? { evidence: str(r.evidence)! } : {}),
      followUpRequired: bool(r.followUpRequired),
    };
  }

  const goalProgress = objArr(o.goalProgressProposals)
    .map((g) => ({
      goalId: str(g.goalId) ?? '',
      proposedStatus: str(g.proposedStatus) ?? '',
      ...(str(g.note) ? { note: str(g.note)! } : {}),
    }))
    .filter((g) => {
      const ok = g.goalId && GOAL_STATUS.has(g.proposedStatus);
      if (!ok && (g.goalId || g.proposedStatus)) warnings.push('목표 진행 제안 중 형식이 맞지 않는 항목을 건너뜀');
      return ok;
    });

  const cf = obj(o.caseFormulationUpdate);
  const formulation = Object.keys(cf).length
    ? {
        currentProblems: strArr(cf.currentProblems),
        added: strArr(cf.addedHypotheses),
        addedItems: Object.fromEntries(
          Object.entries(obj(cf.addedItems))
            .map(([k, v]) => [k, strArr(v)] as const)
            .filter(([k, v]) => FORMULATION_FIELD_KEYS.has(k) && k !== 'beliefHypotheses' && v.length),
        ),
        strengthened: strArr(cf.strengthenedHypotheses),
        weakened: strArr(cf.weakenedHypotheses),
        rejected: strArr(cf.rejectedHypotheses),
        ...(str(cf.changeReason) ? { changeReason: str(cf.changeReason)! } : {}),
      }
    : null;

  const dx = obj(o.diagnosticConsiderationUpdate);
  const diagnosis = Object.keys(dx).length
    ? {
        symptomList: strArr(dx.symptomList),
        considerations: objArr(dx.considerations)
          .map((c) => ({
            diagnosisName: str(c.diagnosisName) ?? '',
            status: DIAG_STATUS.has(str(c.status) ?? '') ? str(c.status)! : 'under_review',
            criteriaToAssess: strArr(c.criteriaToAssess),
            differentialConsiderations: strArr(c.differentialConsiderations),
          }))
          .filter((c) => c.diagnosisName),
      }
    : null;

  const jd = obj(o.journalDraft);
  const journalDraft: Record<string, string> = {};
  for (const k of JOURNAL_KEYS) if (str(jd[k])) journalDraft[k] = str(jd[k])!;

  const nb = obj(o.nextSessionBrief);
  const changePoints: ChangePoint[] = objArr(o.changePoints)
    .map((c) => ({
      type: str(c.type) ?? '변화',
      ...(str(c.theory) ? { theory: str(c.theory)! } : {}),
      startSegmentId: str(c.startSegmentId) ?? '',
      endSegmentId: str(c.endSegmentId) ?? '',
      beforeSegmentIds: strArr(c.beforeSegmentIds),
      afterSegmentIds: strArr(c.afterSegmentIds),
      ...(str(c.whatChanged) ? { whatChanged: str(c.whatChanged)! } : {}),
      ...(str(c.meaning) ? { meaning: str(c.meaning)! } : {}),
      supervisionQuestions: strArr(c.supervisionQuestions),
    }))
    .filter((c) => c.startSegmentId && c.endSegmentId);
  const cpToSuggestion = (c: ChangePoint): Record<string, unknown> => ({
    startSegmentId: c.startSegmentId,
    endSegmentId: c.endSegmentId,
    reason: [`[${c.type}${c.theory ? ` · ${c.theory}` : ''}]`, c.whatChanged, c.meaning].filter(Boolean).join(' '),
    supervisionQuestions: c.supervisionQuestions,
  });
  const dd = obj(o.dialogueDraft);
  const dialogueDraft: Record<string, string> = {};
  for (const k of DIALOGUE_KEYS) {
    const v = str(dd[k]) ?? (Array.isArray(dd[k]) ? strArr(dd[k]).join('\n') : undefined);
    if (v) dialogueDraft[k] = v;
  }
  const sr = obj(o.selfReview);
  const selfReview = Object.keys(sr).length
    ? (Object.fromEntries(REVIEW_KEYS.map((k) => [k, strArr(sr[k])])) as SecondReviewV1)
    : null;
  const dp = obj(o.directionPlan);
  const directionPlan = Object.keys(dp).length
    ? {
        ...(str(dp.summary) ? { summary: str(dp.summary)! } : {}),
        steps: objArr(dp.steps)
          .map((x) => ({
            technique: str(x.technique) ?? '',
            ...(str(x.theory) ? { theory: str(x.theory)! } : {}),
            ...(str(x.method) ? { method: str(x.method)! } : {}),
            ...(str(x.example) ? { example: str(x.example)! } : {}),
            ...(str(x.caution) ? { caution: str(x.caution)! } : {}),
          }))
          .filter((x) => x.technique),
      }
    : null;
  const missingSections = j.truncated ? SESSION_SECTIONS.filter((k) => !(k in o)) : [];

  const value: SessionAnalysisV1 = {
    ...(str(o.clientCode) ? { clientCode: str(o.clientCode)! } : {}),
    ...(typeof o.sessionNumber === 'number' ? { sessionNumber: o.sessionNumber } : {}),
    ...(str(o.sessionSummary) ? { sessionSummary: str(o.sessionSummary)! } : {}),
    keyEvents: strArr(o.keyEvents),
    notableChanges: strArr(o.notableChanges),
    clientReports: objArr(o.clientReports)
      .map((c) => ({ text: str(c.text) ?? '', segmentIds: strArr(c.segmentIds) }))
      .filter((c) => c.text),
    counselorObservations: [
      ...objArr(o.counselorObservations).map((c) => ({ text: str(c.text) ?? '' })),
      ...strArr(
        Array.isArray(o.counselorObservations) ? o.counselorObservations.filter((x) => typeof x === 'string') : [],
      ).map((text) => ({ text })),
    ].filter((c) => c.text),
    hypotheses: objArr(o.hypotheses)
      .map((h) => ({ text: str(h.text) ?? '', segmentIds: strArr(h.supportingSegmentIds ?? h.segmentIds) }))
      .filter((h) => h.text),
    emotions: strArr(o.emotions),
    cognitions: strArr(o.cognitions),
    behaviors: strArr(o.behaviors),
    relationshipPatterns: strArr(o.relationshipPatterns),
    interventionsUsed: objArr(o.interventionsUsed)
      .map((x) => ({
        name: str(x.name ?? x.interventionName) ?? '',
        ...(str(x.orientation) ? { orientation: str(x.orientation)! } : {}),
        ...(str(x.clientResponse) ? { clientResponse: str(x.clientResponse)! } : {}),
        followUpNeeded: bool(x.followUpNeeded),
      }))
      .filter((x) => x.name),
    risk,
    protectiveFactors: strArr(o.protectiveFactors),
    goalProgress,
    formulation,
    diagnosis,
    journalDraft,
    nextSessionBrief: {
      sessionObjectives: strArr(nb.sessionObjectives),
      assessmentQuestions: objArr(nb.assessmentQuestions)
        .map((q) => ({ question: str(q.question) ?? '', ...(str(q.purpose) ? { purpose: str(q.purpose)! } : {}) }))
        .filter((q) => q.question),
      hypothesesToTest: strArr(nb.hypothesesToTest),
      riskFollowUp: strArr(nb.riskFollowUp),
      unfinishedTopics: strArr(nb.unfinishedTopics),
    },
    recommendedInterventions: objArr(o.recommendedInterventions)
      .map((x) => ({
        intervention: str(x.intervention) ?? '',
        ...(str(x.orientation) ? { orientation: str(x.orientation)! } : {}),
        ...(str(x.rationale) ? { rationale: str(x.rationale)! } : {}),
        ...(str(x.expectedGoal) ? { expectedGoal: str(x.expectedGoal)! } : {}),
        ...(str(x.howTo) ? { howTo: str(x.howTo)! } : {}),
        ...(str(x.caution) ? { caution: str(x.caution)! } : {}),
      }))
      .filter((x) => x.intervention),
    roleReviewProposals: objArr(o.roleReviewProposals)
      .map((r) => ({
        segmentId: str(r.segmentId) ?? '',
        proposedRole: str(r.proposedRole) as 'counselor' | 'client',
        ...(str(r.reason) ? { reason: str(r.reason)! } : {}),
      }))
      .filter((r) => r.segmentId && (r.proposedRole === 'counselor' || r.proposedRole === 'client')),
    approachEvidence: parseApproachEvidence(o.approachEvidence),
    verbatimSuggestions: [...changePoints.map(cpToSuggestion), ...objArr(o.verbatimSuggestions)]
      .map((v) => ({
        startSegmentId: str(v.startSegmentId) ?? '',
        endSegmentId: str(v.endSegmentId) ?? '',
        ...(str(v.reason) ? { reason: str(v.reason)! } : {}),
        supervisionQuestions: strArr(v.supervisionQuestions),
      }))
      .filter((v) => v.startSegmentId && v.endSegmentId),
    questionsForCounselorReview: strArr(o.questionsForCounselorReview),
    profileUpdates: objArr(o.clientProfileUpdates)
      .map((p) => ({
        field: str(p.field) ?? '',
        value: str(p.value) ?? '',
        segmentIds: strArr(p.segmentIds),
        ...(p.change === 'changed' || p.changed === true ? { changed: true } : {}),
      }))
      .filter((p): p is { field: ProfileField; value: string; segmentIds: string[]; changed?: true } => {
        const ok = isProfileField(p.field) && !!p.value && p.value.length <= 300;
        if (!ok && (p.field || p.value)) warnings.push('내담자 정보 제안 중 형식이 맞지 않는 항목을 건너뜀');
        return ok;
      }),
    changePoints,
    goalSuggestions: objArr(o.goalSuggestions)
      .map((g) => ({
        title: str(g.title) ?? '',
        agreedWithClient: g.agreedWithClient !== false && g.agreedWithClient !== 'false',
        ...(str(g.rationale) ? { rationale: str(g.rationale)! } : {}),
      }))
      .filter((g) => g.title && g.title.length <= 200),
    dialogueDraft,
    selfReview: selfReview && REVIEW_KEYS.some((k) => selfReview[k].length) ? selfReview : null,
    directionPlan: directionPlan && (directionPlan.summary || directionPlan.steps.length) ? directionPlan : null,
    missingSections,
    truncated: !!j.truncated,
  };
  // 방향 계획의 단계는 다음 회기 준비의 기법 제안으로도 합친다
  if (value.directionPlan)
    for (const st of value.directionPlan.steps)
      value.recommendedInterventions.push({
        intervention: st.technique,
        ...(st.theory ? { orientation: st.theory } : {}),
        rationale: `상담 방향: ${value.directionPlan.summary ?? '상담자가 원하는 방향'}`,
        ...(st.method || st.example
          ? { howTo: [st.method, st.example && `예: ${st.example}`].filter(Boolean).join(' / ') }
          : {}),
        ...(st.caution ? { caution: st.caution } : {}),
      });
  const empty =
    !value.sessionSummary &&
    !value.keyEvents.length &&
    !Object.keys(journalDraft).length &&
    !value.changePoints.length &&
    !value.selfReview &&
    !Object.keys(value.dialogueDraft).length &&
    !value.directionPlan &&
    !value.profileUpdates.length;
  if (empty) return { ok: false, errors: ['분석 내용이 비어 있습니다'] };
  return { ok: true, value, warnings };
}

export type ParsedApproachEvidence = SessionAnalysisV1['approachEvidence'][number];

/** 치료 접근 근거 목록 읽기 (A33): 별칭·한국어 종류를 받아들이고, 모르는 접근·빈 인용은 건너뛴다 */
export function parseApproachEvidence(v: unknown): ParsedApproachEvidence[] {
  return objArr(v)
    .map((x) => {
      const approach = approachIdOf(str(x.approach) ?? '');
      const kind = evidenceKindOf(str(x.kind) ?? '');
      const quote = (str(x.quote ?? x.text) ?? '').slice(0, 1000);
      const reason = str(x.reason)?.slice(0, 1000);
      return approach && kind && quote
        ? { approach, kind, quote, ...(reason ? { reason } : {}), segmentIds: strArr(x.segmentIds).slice(0, 20) }
        : null;
    })
    .filter((x): x is NonNullable<typeof x> => !!x);
}

/** 이전 회기 근거 한꺼번에 (APPROACH_EVIDENCE_V1): 회기 번호별 근거 */
export function parseApproachBackfill(
  raw: string,
): ParseOk<{ sessions: { sessionNumber: number; items: ParsedApproachEvidence[] }[] }> | ParseFail {
  const j = parseJsonLoose(raw);
  if (!j.ok) return { ok: false, errors: [j.error] };
  const o = obj(j.value);
  const sessions = objArr(o.sessions)
    .map((s) => ({ sessionNumber: Number(s.sessionNumber), items: parseApproachEvidence(s.approachEvidence) }))
    .filter((s) => Number.isInteger(s.sessionNumber) && s.sessionNumber > 0);
  if (!sessions.length) return { ok: false, errors: ['회기별 근거(sessions)가 없습니다'] };
  const warnings = [
    ...(o.formatVersion === 'APPROACH_EVIDENCE_V1' ? [] : ['formatVersion 없음 (APPROACH_EVIDENCE_V1로 간주)']),
    ...(j.truncated ? ['응답이 중간에 끊겼습니다 — 받은 회기만 넣습니다'] : []),
  ];
  return { ok: true, value: { sessions }, warnings };
}

export type SecondReviewV1 = Record<
  | 'unsupportedInference'
  | 'alternativeHypotheses'
  | 'missingInformation'
  | 'contradictoryEvidence'
  | 'theoryBias'
  | 'interventionConcerns'
  | 'counselorProcessQuestions'
  | 'supervisionQuestions',
  string[]
>;

export function parseSecondReview(raw: string): ParseOk<SecondReviewV1> | ParseFail {
  const j = parseJsonLoose(raw);
  if (!j.ok) return { ok: false, errors: [j.error] };
  const o = obj(j.value);
  const keys = [
    'unsupportedInference',
    'alternativeHypotheses',
    'missingInformation',
    'contradictoryEvidence',
    'theoryBias',
    'interventionConcerns',
    'counselorProcessQuestions',
    'supervisionQuestions',
  ] as const;
  const value = Object.fromEntries(keys.map((k) => [k, strArr(o[k])])) as SecondReviewV1;
  if (keys.every((k) => value[k].length === 0)) return { ok: false, errors: ['검토 내용이 비어 있습니다'] };
  return {
    ok: true,
    value,
    warnings: o.formatVersion === 'SECOND_REVIEW_V1' ? [] : ['formatVersion 없음 (SECOND_REVIEW_V1로 간주)'],
  };
}
